// POLineasXL - Sprint 44 Fase 3 Vista 9.
// Editor lineas PO. Reusa ProductPickerLite legacy. XLMoneyInput precio.
import { useCallback, useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import apiClient from '../../api/apiClient';
import ProductPickerLite from '../../components/shared/ProductPickerLite';
import XLMoneyInput from '../../components/xl/XLMoneyInput';
import XLSkeleton from '../../components/xl/XLSkeleton';
import { fmtMoney } from '../../utils/money';

const CSS_ID = 'xl-po-lineas-css';
const CSS = `
.xl-pol { display: flex; flex-direction: column; gap: 14px; font-family: var(--xl-font-sans) }
.xl-pol__addbar {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 14px 16px;
}
.xl-pol__addbar-lbl {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary); margin-bottom: 6px;
}
.xl-pol__addbar-hint {
  font-size: 11px; color: var(--xl-color-text-muted); margin-top: 4px;
}
.xl-pol__title {
  font-size: 13px; font-weight: 600;
  color: var(--xl-color-text); margin: 0 0 8px;
}
.xl-pol__table-wrap {
  overflow-x: auto;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: var(--xl-color-surface);
  box-shadow: var(--xl-shadow-sm);
}
.xl-pol__table { width: 100%; min-width: 640px; border-collapse: collapse; font-size: 13px }
.xl-pol__table thead th {
  background: var(--xl-color-bg);
  color: var(--xl-color-text-secondary);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  white-space: nowrap;
}
.xl-pol__table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  color: var(--xl-color-text);
}
.xl-pol__table tr:last-child td { border-bottom: 0 }
.xl-pol__numinp {
  width: 90px;
  font: 400 13px/1.4 var(--xl-font-mono);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  padding: 6px 8px;
  text-align: right;
  box-sizing: border-box;
}
.xl-pol__numinp:focus { outline: none; border-color: var(--xl-color-focus); box-shadow: 0 0 0 3px rgba(37,99,235,0.18) }
[data-xl-theme="dark"] .xl-pol__numinp:focus { box-shadow: 0 0 0 3px rgba(96,165,250,0.28) }
.xl-pol__del {
  background: transparent; border: none; cursor: pointer;
  color: var(--xl-color-danger); font-size: 13px;
  padding: 4px 8px; border-radius: var(--xl-radius-sm);
}
.xl-pol__del:hover { background: rgba(220,38,38,0.08) }
.xl-pol__empty { padding: 28px 16px; text-align: center; color: var(--xl-color-text-muted); font-size: 13px }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-td-right { text-align: right }
.xl-pol__msg {
  font-size: 12px; padding: 8px 12px;
  background: rgba(37,99,235,0.10); color: var(--xl-color-info);
  border: 1px solid rgba(37,99,235,0.28); border-radius: var(--xl-radius-sm);
}
`;

export default function POLineasXL({ poId, moneda = 'USD' }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const r = await apiClient.get('/po/' + poId + '/lineas');
      setLineas((r && r.data && r.data.items) || []);
    } catch (_) { setLineas([]); }
    finally { setLoading(false); }
  }, [poId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  const onAdd = async (p) => {
    try {
      const r = await apiClient.post('/po/' + poId + '/lineas', {
        producto_id: p.id,
        cantidad: 1,
        precio_unitario_moneda: Number(p.purchase_cost || p.precio_venta || 0) || 0.01,
      });
      if (r && r.data) setLineas((prev) => [...prev, r.data]);
    } catch (e) { showToast((e && e.data && e.data.message) || (e && e.message) || 'No se pudo agregar'); }
  };

  const onLocalChange = (lid, field, value) =>
    setLineas((p) => p.map((l) => l.id === lid ? { ...l, [field]: value } : l));

  const onSave = async (linea) => {
    try {
      await apiClient.put('/po/lineas/' + linea.id, {
        producto_id: linea.producto_id,
        cantidad: Number(linea.cantidad) || 0,
        precio_unitario_moneda: Number(linea.precio_unitario_moneda) || 0,
      });
    } catch (e) { showToast((e && e.message) || 'No se pudo guardar'); }
  };

  const onDelete = async (lid) => {
    if (!window.confirm('Eliminar linea?')) return;
    try {
      await apiClient.delete('/po/lineas/' + lid);
      setLineas((p) => p.filter((l) => l.id !== lid));
    } catch (e) { showToast((e && e.message) || 'No se pudo eliminar'); }
  };

  return (
    <div className="xl-pol">
      <h3 className="xl-pol__title">Lineas de productos</h3>

      <div className="xl-pol__addbar">
        <div className="xl-pol__addbar-lbl">Agregar producto a la PO</div>
        <ProductPickerLite key={lineas.length} onPick={onAdd} />
        <div className="xl-pol__addbar-hint">
          Escribe el codigo o nombre. Al seleccionar, se agrega como nueva linea.
        </div>
      </div>

      {toast && <div className="xl-pol__msg" role="status">{toast}</div>}

      <div className="xl-pol__table-wrap">
        <table className="xl-pol__table">
          <thead>
            <tr>
              <th>Producto</th>
              <th className="xl-td-right">Cantidad</th>
              <th className="xl-td-right">Precio unitario</th>
              <th className="xl-td-right">Subtotal</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 2 }).map((_, i) => (
              <tr key={'sk-' + i}>
                <td colSpan={5}><XLSkeleton variant="line" width="80%" height={18} /></td>
              </tr>
            ))}
            {!loading && lineas.length === 0 && (
              <tr><td colSpan={5} className="xl-pol__empty">Sin lineas. Agrega productos arriba.</td></tr>
            )}
            {!loading && lineas.map((l) => {
              const subtotal = Number(l.cantidad || 0) * Number(l.precio_unitario_moneda || 0);
              return (
                <tr key={l.id}>
                  <td>{l.producto_nombre || ('#' + (l.producto_id || ''))}</td>
                  <td className="xl-td-right">
                    <input type="number" min="0" step="0.01" className="xl-pol__numinp"
                      value={l.cantidad || 0}
                      onChange={(e) => onLocalChange(l.id, 'cantidad', e.target.value)}
                      onBlur={() => onSave(l)} />
                  </td>
                  <td className="xl-td-right">
                    <XLMoneyInput value={l.precio_unitario_moneda || 0}
                      onChange={(n) => { onLocalChange(l.id, 'precio_unitario_moneda', n); onSave({ ...l, precio_unitario_moneda: n }); }} />
                  </td>
                  <td className="xl-td-right xl-mono">{fmtMoney(subtotal, moneda)}</td>
                  <td className="xl-td-right">
                    <button type="button" className="xl-pol__del"
                      onClick={() => onDelete(l.id)} aria-label="Eliminar">X</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
